import React, { memo } from "react";
import PropTypes from "prop-types";

const normalizeSize = (value, fallback) => {
  if (typeof value === "number") {
    return `${value}px`;
  }

  if (typeof value === "string" && value.trim()) {
    const trimmedValue = value.trim();
    return /^\d+(\.\d+)?$/.test(trimmedValue)
      ? `${trimmedValue}px`
      : trimmedValue;
  }

  return fallback;
};

const Loader = ({
  color = "#8347ad",
  height = 48,
  width = 48,
  className = "",
  ariaLabel = "Loading",
}) => {
  const resolvedHeight = normalizeSize(height, "48px");
  const resolvedWidth = normalizeSize(width, "48px");
  const rootClassName = className ? `loader ${className}` : "loader";

  return (
    <span className={rootClassName} role="status" aria-label={ariaLabel}>
      <svg
        className="spinner"
        viewBox="0 0 50 50"
        style={{ height: resolvedHeight, width: resolvedWidth }}
      >
        <circle
          className="track"
          cx="25"
          cy="25"
          r="20"
          fill="none"
          stroke={color}
          strokeWidth="4"
        />
        <circle
          className="indicator"
          cx="25"
          cy="25"
          r="20"
          fill="none"
          stroke={color}
          strokeLinecap="round"
          strokeWidth="4"
        />
      </svg>
      <style jsx>{`
        .loader {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          line-height: 0;
        }

        .spinner {
          animation: spinner-rotate 1.1s linear infinite;
          display: block;
          transform-origin: center;
        }

        .track {
          opacity: 0.2;
        }

        .indicator {
          animation: spinner-dash 1.6s ease-in-out infinite;
          stroke-dasharray: 90 150;
          stroke-dashoffset: 0;
          transform-origin: center;
        }

        @keyframes spinner-rotate {
          100% {
            transform: rotate(360deg);
          }
        }

        @keyframes spinner-dash {
          0% {
            stroke-dasharray: 1 150;
            stroke-dashoffset: 0;
          }

          50% {
            stroke-dasharray: 90 150;
            stroke-dashoffset: -20;
          }

          100% {
            stroke-dasharray: 90 150;
            stroke-dashoffset: -124;
          }
        }
      `}</style>
    </span>
  );
};

Loader.propTypes = {
  ariaLabel: PropTypes.string,
  className: PropTypes.string,
  color: PropTypes.string,
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default memo(Loader);
